import React, { useEffect, useState } from 'react';

import {
  AlignLeftOutlined,
  FileOutlined,
  FolderOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Modal,
  Row,
  Skeleton,
  Switch,
  Tooltip,
  Tree,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ApiErrorData } from 'models/ApiResponse';
import { MenuNode } from 'models/MenuNode';
import { Role } from 'models/Role';

import { MenuService } from 'services/MenuService';

interface NewMenuDetailModalProps {
  role: Role;
  isOpen: boolean;
  handleModalCancel: () => void;
}

function NewMenuDetailModal({
  role,
  isOpen,
  handleModalCancel,
}: NewMenuDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [treeData, setTreeData] = useState<any[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [treeRenderKey, setTreeRenderKey] = useState<number>(0);
  const [rawNodes, setRawNodes] = useState<MenuNode[]>([]);
  const [inheritedSet, setInheritedSet] = useState<Set<string>>(new Set());
  const [form] = Form.useForm();

  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setInitialLoading(true);

    const response = await MenuService.getSystemMenuNodesWithRoleAccess(
      role.id
    );

    if (response.success) {
      const data = response.data as MenuNode[];
      // Construir conjunto de nodos heredados inicial desde backend (isRecursive)
      const initialInherited = new Set<string>();
      const collectInherited = (arr: MenuNode[]) => {
        arr.forEach((n) => {
          if (n.isRecursive === true) initialInherited.add(n.code);
          if (n.children && n.children.length) collectInherited(n.children);
        });
      };
      collectInherited(data);
      // Construir treeData para Antd Tree
      const builtChildren = buildTreeData(
        data,
        undefined,
        i18n.language,
        false,
        initialInherited
      );
      const rootNode = {
        key: 'root',
        title: window.location.origin,
        selectable: false,
        children: builtChildren,
      };

      setRawNodes(data);
      setTreeData([rootNode]);
      // Incluir en checked los contenedores heredados para reflejar selección inicial
      const baseChecked = new Set<React.Key>(collectSelectedKeys(data));
      initialInherited.forEach((code) => baseChecked.add(code));
      setCheckedKeys([...Array.from(baseChecked), 'root']);
      setInheritedSet(initialInherited);
      // Forzar remount del Tree para que defaultExpandAll se aplique
      setTreeRenderKey((prev) => prev + 1);
      setInitialLoading(false);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.settings.roles.components.new_menu_detail_modal.errors.fetch_menus_role'
          ),
        3.5
      );
    }
  };

  // Transforma MenuNode[] del backend a treeData de Antd
  const buildTreeData = (
    nodes: MenuNode[],
    parentCode?: string,
    currentLang?: string,
    ancestorInherited = false,
    inheritedOverride?: Set<string>
  ): any[] => {
    const normalizedLang = currentLang === 'en' ? 'en' : 'es';
    return nodes.map((n) => {
      const localizedTitle = n.localizedTitles?.[normalizedLang] ?? null;
      const nodeTitle = localizedTitle ?? n.title;
      // Determinar si el nodo actual está en modo heredado
      const isCurrentInherited = (inheritedOverride || inheritedSet).has(
        n.code
      );
      const descendantInheritedFlag = ancestorInherited || isCurrentInherited;
      // Cuando el switch está activo (heredado) en este contenedor, no renderizar sus hijos
      const children =
        Array.isArray(n.children) &&
        n.children.length > 0 &&
        !descendantInheritedFlag
          ? buildTreeData(
              n.children,
              n.code,
              currentLang,
              descendantInheritedFlag,
              inheritedOverride
            )
          : [];
      const iconEl =
        n.menuTypeId === 'PARENT_MENU' ? <FolderOutlined /> : <FileOutlined />;
      const showInheritedSwitch =
        !ancestorInherited &&
        n.companyOwnerId !== null &&
        n.menuTypeId === 'PARENT_MENU';

      const titleNode = (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            width: '100%',
            gap: 6,
          }}
        >
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {nodeTitle}
          </span>
          {isCurrentInherited && (
            <span
              style={{
                backgroundColor: '#e6f4ff',
                borderRadius: 10,
                padding: '0 6px',
                fontSize: 11,
                color: '#0958d9',
                lineHeight: '18px',
                height: 18,
                display: 'inline-flex',
                alignItems: 'center',
              }}
              onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
              onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
              onDoubleClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
            >
              {t(
                'scenes.settings.roles.components.new_menu_detail_modal.chips.inherited_children_access'
              )}
            </span>
          )}
          {showInheritedSwitch && (
            <span
              style={{ marginLeft: 'auto', flexShrink: 0 }}
              onClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
              onMouseDown={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
              onDoubleClick={(e: React.MouseEvent<HTMLSpanElement>) =>
                e.stopPropagation()
              }
            >
              <Tooltip
                title={t(
                  'scenes.settings.roles.components.new_menu_detail_modal.tooltips.inherited_switch'
                )}
              >
                <Switch
                  size="small"
                  checked={isCurrentInherited}
                  onChange={(checked) => toggleInheritedMode(n.code, checked)}
                />
              </Tooltip>
            </span>
          )}
        </span>
      );

      return {
        title: titleNode,
        key: n.code,
        parentCode: parentCode,
        icon: iconEl,
        children,
        // flags para lógica de UI/comportamiento en eventos
        canToggleInherited: showInheritedSwitch,
        isInherited: isCurrentInherited,
        disabled: ancestorInherited, // si algún ancestro está en modo heredado, este nodo queda bloqueado
      };
    });
  };

  // Re-renderizar árbol cuando cambie el idioma, manteniendo selección
  useEffect(() => {
    if (rawNodes && rawNodes.length > 0) {
      const builtChildren = buildTreeData(
        rawNodes,
        undefined,
        i18n.language,
        false,
        inheritedSet
      );
      const rootNode = {
        key: 'root',
        title: window.location.origin,
        selectable: false,
        children: builtChildren,
      };
      setTreeData([rootNode]);
      setTreeRenderKey((prev) => prev + 1);
    }
  }, [i18n.language, rawNodes, inheritedSet]);

  // Obtiene keys seleccionadas según hasPermission
  const collectSelectedKeys = (nodes: MenuNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const walk = (arr: MenuNode[]) => {
      for (const n of arr) {
        if (n.hasPermission) keys.push(n.code);
        if (n.children && n.children.length) walk(n.children);
      }
    };
    walk(nodes);
    return keys;
  };

  const codeToNodeMapRef = React.useRef<Map<string, MenuNode>>(new Map());
  const parentCodeMapRef = React.useRef<Map<string, string | 'root'>>(
    new Map()
  );
  useEffect(() => {
    // mantener un mapa para encontrar descendientes fácilmente
    const map = new Map<string, MenuNode>();
    const walk = (arr: MenuNode[]) => {
      arr.forEach((n) => {
        map.set(n.code, n);
        if (n.children && n.children.length) walk(n.children);
      });
    };
    walk(rawNodes);
    codeToNodeMapRef.current = map;

    // construir mapa de padre -> para propagación ascendente hasta root
    const parentMap = new Map<string, string | 'root'>();
    const buildParentMap = (arr: MenuNode[], parent: string | 'root') => {
      arr.forEach((n) => {
        parentMap.set(n.code, parent);
        if (n.children && n.children.length) buildParentMap(n.children, n.code);
      });
    };
    buildParentMap(rawNodes, 'root');
    parentCodeMapRef.current = parentMap;
  }, [rawNodes]);

  const collectDescendantCodesFromRaw = (code: string): string[] => {
    const acc: string[] = [];
    const start = codeToNodeMapRef.current.get(code);
    const walk = (n?: MenuNode) => {
      if (!n || !n.children) return;
      n.children.forEach((c) => {
        acc.push(c.code);
        walk(c);
      });
    };
    walk(start);
    return acc;
  };

  const toggleInheritedMode = (code: string, inherited: boolean) => {
    // calcular el siguiente conjunto heredado inmediatamente
    const nextInherited = new Set(inheritedSet);
    if (inherited) nextInherited.add(code);
    else nextInherited.delete(code);
    setInheritedSet(nextInherited);

    setCheckedKeys((prev) => {
      const next = new Set<React.Key>(prev);
      if (inherited) {
        // Cuando se activa el modo heredado, el contenedor debe quedar marcado
        next.add(code);
      }
      if (!inherited) {
        // Al volver a Manual, mantenemos los checks existentes.
        // Solo retiramos el estado bloqueado a través del re-render del árbol.
        // No removemos marcados automáticos para preservar selecciones del usuario.
      }
      next.add('root');
      return Array.from(next);
    });

    // No reconstruimos el árbol aquí; el useEffect anterior lo hará de forma estable
  };

  const onFinish = async () => {
    setLoading(true);

    try {
      // Obtener códigos seleccionados (sin root)
      const selectedCodes = checkedKeys
        .filter((k) => k !== 'root')
        .map((k) => String(k));

      // Construir conjunto de descendientes de todos los seleccionados con modo heredado (isRecursive)
      const recursiveRoots = selectedCodes.filter((code) =>
        inheritedSet.has(code)
      );
      const descendantsToOmit = new Set<string>();
      recursiveRoots.forEach((code) => {
        collectDescendantCodesFromRaw(code).forEach((childCode) =>
          descendantsToOmit.add(childCode)
        );
      });

      // Filtrar hijos de contenedores recursivos para no enviarlos explícitamente
      const finalCodes = selectedCodes.filter(
        (code) => !descendantsToOmit.has(code)
      );

      // Armar payload con { code, isRecursive }
      const menus = finalCodes.map((code) => ({
        code,
        isRecursive: inheritedSet.has(code),
      }));

      const response = await MenuService.associateMenusWithRole({
        roleId: role.id,
        menus,
      });

      if (response.success) {
        message.success(
          t(
            'scenes.settings.roles.components.new_menu_detail_modal.messages.save_success'
          ),
          2.5
        );
        handleModalCancel();
      } else {
        const err = response.data as ApiErrorData;
        message.error(
          (err?.message as string) ||
            t(
              'scenes.settings.roles.components.new_menu_detail_modal.errors.save_failed'
            ),
          3.5
        );
      }
    } catch (error: any) {
      message.error(
        t(
          'scenes.settings.roles.components.new_menu_detail_modal.errors.save_unexpected'
        ),
        3.5
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Modal
        title={
          <>
            <AlignLeftOutlined style={{ marginRight: '5px' }} />
            {t('scenes.settings.roles.components.new_menu_detail_modal.title', {
              roleName: role.name,
            })}
          </>
        }
        open={isOpen}
        onCancel={handleModalCancel}
        maskClosable={false}
        destroyOnClose
        footer={null}
        width={640}
      >
        <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={24} style={{ marginBottom: '18px' }}>
            <Col xs={24}>
              <Alert
                message={t(
                  'scenes.settings.roles.components.new_menu_detail_modal.alert_info'
                )}
                type="info"
                showIcon
              />
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: '18px' }}>
            <Col xs={24} lg={24}>
              {/* estilos escopados para alinear icono, título y switch en una sola línea */}
              <style>
                {`
                /* Alinear icono, título y switch en una sola línea y usar todo el ancho */
                .role-menu-tree .ant-tree-node-content-wrapper {
                  display: inline-flex !important;
                  align-items: center;
                  width: 100% !important;
                }
                .role-menu-tree .ant-tree-title {
                  display: inline-flex !important;
                  align-items: center;
                  width: 100% !important;
                  gap: 6px;
                }
                .role-menu-tree .ant-tree-icon__customize { margin-right: 6px; }
                `}
              </style>
              {initialLoading ? (
                <Skeleton active paragraph={{ rows: 6 }} />
              ) : (
                <Tree
                  className="role-menu-tree"
                  key={treeRenderKey}
                  showIcon
                  showLine
                  checkable
                  checkStrictly
                  selectable={false}
                  defaultExpandAll
                  blockNode
                  style={{
                    pointerEvents: loading ? 'none' : 'auto',
                    opacity: loading ? 0.7 : 1,
                  }}
                  treeData={treeData}
                  checkedKeys={checkedKeys}
                  onCheck={(checkedParam, info) => {
                    if (loading) return; // desactivar interacción durante submit
                    if ((info.node as any).disabled) return; // ignorar nodos bloqueados por modo heredado
                    // Lógica personalizada sin estados indeterminados
                    const nodeKey = info.node.key as string;
                    if (nodeKey === 'root') {
                      // Mantener siempre marcado el root
                      setCheckedKeys((prev) =>
                        Array.from(new Set([...prev, 'root']))
                      );
                      return;
                    }
                    const isBranch =
                      Array.isArray(info.node.children) &&
                      info.node.children.length > 0;
                    const next = new Set<React.Key>(checkedKeys);
                    const currentlyChecked = next.has(nodeKey);

                    const collectDescendantKeys = (node: any): React.Key[] => {
                      const acc: React.Key[] = [];
                      const walk = (n: any) => {
                        (n.children || []).forEach((child: any) => {
                          acc.push(child.key);
                          walk(child);
                        });
                      };
                      walk(node);
                      return acc;
                    };

                    if (!currentlyChecked) {
                      // Marcar nodo actual
                      next.add(nodeKey);
                      // Si es rama, marcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.add(k)
                        );
                      }
                      // Propagar check hacia todos los ancestros hasta root
                      let parentCode = parentCodeMapRef.current.get(nodeKey);
                      while (parentCode && parentCode !== 'root') {
                        next.add(parentCode);
                        parentCode = parentCodeMapRef.current.get(parentCode);
                      }
                    } else {
                      // Desmarcar nodo actual
                      next.delete(nodeKey);
                      // Si es rama, desmarcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.delete(k)
                        );
                      }

                      // Si el nodo tiene switch visible y está en modo heredado,
                      // al desmarcar su checkbox se desactiva inmediatamente el modo heredado
                      const nodeAny = info.node as any;
                      if (nodeAny.canToggleInherited && nodeAny.isInherited) {
                        toggleInheritedMode(nodeKey, false);
                      }
                    }

                    next.add('root');
                    setCheckedKeys(Array.from(next));
                  }}
                  onSelect={(keys, info) => {
                    if (loading) return; // desactivar interacción durante submit
                    if ((info.node as any).disabled) return; // ignorar nodos bloqueados por modo heredado
                    // Al hacer clic en el título, alternamos el checkbox usando la misma lógica
                    const nodeKey = info.node.key as string;
                    if (nodeKey === 'root') {
                      // Mantener siempre marcado el root
                      setCheckedKeys((prev) =>
                        Array.from(new Set([...prev, 'root']))
                      );
                      return; // ignorar cambios adicionales
                    }

                    const isBranch =
                      Array.isArray(info.node.children) &&
                      info.node.children.length > 0;
                    const next = new Set<React.Key>(checkedKeys);
                    const currentlyChecked = next.has(nodeKey);

                    const collectDescendantKeys = (node: any): React.Key[] => {
                      const acc: React.Key[] = [];
                      const walk = (n: any) => {
                        (n.children || []).forEach((child: any) => {
                          acc.push(child.key);
                          walk(child);
                        });
                      };
                      walk(node);
                      return acc;
                    };

                    if (!currentlyChecked) {
                      // Marcar nodo actual
                      next.add(nodeKey);
                      // Si es rama, marcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.add(k)
                        );
                      }
                      // Propagar check hacia todos los ancestros hasta root
                      let parentCode = parentCodeMapRef.current.get(nodeKey);
                      while (parentCode && parentCode !== 'root') {
                        next.add(parentCode);
                        parentCode = parentCodeMapRef.current.get(parentCode);
                      }
                    } else {
                      // Desmarcar nodo actual
                      next.delete(nodeKey);
                      // Si es rama, desmarcar todos sus hijos
                      if (isBranch) {
                        collectDescendantKeys(info.node).forEach((k) =>
                          next.delete(k)
                        );
                      }
                    }

                    next.add('root');
                    setCheckedKeys(Array.from(next));
                  }}
                />
              )}
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Button
                onClick={() => {
                  form.submit();
                }}
                loading={loading}
                type="primary"
                block
              >
                {t(
                  'scenes.settings.roles.components.new_menu_detail_modal.buttons.save_changes'
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default NewMenuDetailModal;
